import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { loadExternalScript } from 'shared-utils/utils/loadExternalScript';
import { SignupFormState } from '../components/signup/types';

// Chili Piper script configuration
export const CHILI_PIPER_SCRIPT_ID = 'chilipiper-concierge';
export const CHILI_PIPER_SCRIPT_SRC = 'https://tandem.chilipiper.com/concierge-js/cjs/concierge.js';
export const CHILI_PIPER_DOMAIN = 'tandem';

// Shared error message for Chili Piper loading failures
export const CHILI_PIPER_LOAD_ERROR_MESSAGE =
  'Unable to load booking calendar. Please refresh the page or try again later.';

/**
 * Available Chili Piper routers for Tandem
 * - inbound-router: For scheduling demos during signup flow
 * - training-router: For scheduling training sessions for existing users
 */
export type ChiliPiperRouter = 'inbound-router' | 'training-router';

/**
 * Chili Piper trigger types
 * @see https://help.chilipiper.com/hc/en-us/articles/32588330506643-Concierge-Snippet-and-JS-API
 * - ThirdPartyForm: For form submissions (e.g., signup flow)
 * - InAppButton: For in-app button clicks (e.g., schedule training)
 */
export type ChiliPiperTrigger = 'ThirdPartyForm' | 'InAppButton';

declare global {
  interface Window {
    ChiliPiper?: {
      submit: (domain: string, router: ChiliPiperRouter, options: ChiliPiperSubmitOptions) => void;
      closePopup: () => void;
    };
  }
}

/**
 * Reset Chili Piper's router state.
 * Must be called before launching Chili Piper again on the same page.
 * @see https://help.chilipiper.com/hc/en-us/articles/32588330506643-Concierge-Snippet-and-JS-API
 */
export const resetChiliPiper = (): void => {
  if (typeof window !== 'undefined' && window.ChiliPiper) {
    window.ChiliPiper.closePopup();
  }
};

export type ChiliPiperSubmitOptions = {
  trigger: ChiliPiperTrigger;
  lead: Record<string, string | number | undefined>;
  domElement?: string; // CSS selector string (e.g., "#container-id")
  onSuccess?: (data?: unknown) => void;
  onError?: (error?: unknown) => void;
  onClose?: () => void;
  onRouted?: () => void;
};

export type ChiliPiperLeadData = {
  email?: string;
  first_name?: string;
  last_name?: string;
  practice_name?: string;
  role?: string;
  affiliation?: string;
  specialty?: string;
  size_numeric?: number;
  organization_type?: string;
  source?: string;
};

/**
 * Load the Chili Piper Concierge script.
 * Returns a promise that resolves when the script is loaded.
 */
export const loadChiliPiperScript = (): Promise<void> => {
  return loadExternalScript({
    id: CHILI_PIPER_SCRIPT_ID,
    src: CHILI_PIPER_SCRIPT_SRC,
    type: 'text/javascript',
  });
};

/**
 * Build lead data object from form state for Chili Piper submission.
 * Only includes fields that Chili Piper routing is configured to use.
 * Filters out undefined/null values to keep the payload clean.
 */
export const buildChiliPiperLeadData = (formState?: SignupFormState): ChiliPiperLeadData => {
  if (!formState) {
    return {};
  }

  // Explicitly pick only the fields Chili Piper needs (avoid sending sensitive PII like npi, phone, etc.)
  const leadData: ChiliPiperLeadData = {
    email: formState.email,
    first_name: formState.first_name,
    last_name: formState.last_name,
    practice_name: formState.practice_name,
    role: formState.role,
    affiliation: formState.affiliation,
    specialty: formState.specialty,
    size_numeric: formState.size_numeric,
    organization_type: formState.organization_type,
    source: formState.source,
  };

  return Object.fromEntries(
    Object.entries(leadData).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  );
};

type SubmitToChiliPiperOptions = {
  router: ChiliPiperRouter;
  trigger: ChiliPiperTrigger;
  domElement?: string; // CSS selector string (e.g., "#container-id") for inline embed
  onSuccess?: (data?: unknown) => void;
  onError?: (error?: unknown) => void;
  onClose?: () => void;
  onRouted?: () => void;
};

/**
 * Submit lead data to Chili Piper to display the scheduling calendar.
 * If domElement is provided (CSS selector), the calendar renders inline in that element.
 * Otherwise, it opens as a modal popup.
 */
export const submitToChiliPiper = (
  leadData: Record<string, string | number | undefined>,
  options: SubmitToChiliPiperOptions,
): void => {
  if (typeof window === 'undefined' || !window.ChiliPiper) {
    const error = new Error('ChiliPiper is not loaded');
    console.error(error.message);
    Sentry.captureException(error);
    options.onError?.(error);
    return;
  }

  const submitOptions: ChiliPiperSubmitOptions = {
    trigger: options.trigger,
    lead: leadData,
    onSuccess: options.onSuccess,
    onError: options.onError,
    onClose: options.onClose,
    onRouted: options.onRouted,
  };

  // Add domElement for inline embed if provided
  if (options.domElement) {
    submitOptions.domElement = options.domElement;
  }

  window.ChiliPiper.submit(CHILI_PIPER_DOMAIN, options.router, submitOptions);
};

export type UseChiliPiperOptions = {
  /**
   * Whether the hook should initialize Chili Piper. Useful for modals where you want
   * to wait until the modal is open before loading.
   */
  enabled?: boolean;
  /**
   * The DOM element ID where Chili Piper will render the calendar (without #).
   */
  containerId: string;
  /**
   * The Chili Piper router to use.
   */
  router: ChiliPiperRouter;
  /**
   * The trigger type for Chili Piper.
   */
  trigger: ChiliPiperTrigger;
  /**
   * Lead data to submit to Chili Piper.
   */
  leadData: Record<string, string | number | undefined>;
  /**
   * Callback when a booking is successfully made.
   */
  onSuccess: () => void;
};

export type UseChiliPiperReturn = {
  /**
   * Whether the calendar is still loading.
   */
  isLoading: boolean;
  /**
   * Whether there was an error loading the script or calendar.
   */
  scriptError: boolean;
  /**
   * Reset the hook state. Call this when closing a modal to allow re-initialization.
   */
  reset: () => void;
};

/**
 * Hook to manage Chili Piper calendar initialization and state.
 * Handles script loading, submission, and cleanup.
 */
export const useChiliPiper = ({
  enabled = true,
  containerId,
  router,
  trigger,
  leadData,
  onSuccess,
}: UseChiliPiperOptions): UseChiliPiperReturn => {
  const [isLoading, setIsLoading] = useState(true);
  const [scriptError, setScriptError] = useState(false);
  const hasSubmittedRef = useRef(false);

  // Use refs for callbacks to avoid re-running the effect when they change
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const leadDataRef = useRef(leadData);
  leadDataRef.current = leadData;

  const reset = useCallback(() => {
    resetChiliPiper();
    hasSubmittedRef.current = false;
    setIsLoading(true);
    setScriptError(false);
  }, []);

  useEffect(() => {
    if (!enabled || hasSubmittedRef.current) {
      return;
    }

    let isMounted = true;

    const initChiliPiper = async () => {
      try {
        await loadChiliPiperScript();

        // Wait a tick to ensure the DOM element is available
        await new Promise((resolve) => setTimeout(resolve, 100));

        if (!isMounted || hasSubmittedRef.current) {
          return;
        }

        // Mark as submitted immediately (synchronous) to prevent double-calls
        hasSubmittedRef.current = true;

        submitToChiliPiper(leadDataRef.current, {
          router,
          trigger,
          domElement: `#${containerId}`,
          onSuccess: () => {
            if (isMounted) {
              onSuccessRef.current();
            }
          },
          onError: (error) => {
            console.error('Chili Piper error:', error);
            if (isMounted) {
              setScriptError(true);
              setIsLoading(false);
            }
          },
          onRouted: () => {
            // Calendar is now displayed
            if (isMounted) {
              setIsLoading(false);
            }
          },
        });
      } catch (error) {
        console.error('Failed to load Chili Piper script:', error);
        if (isMounted) {
          setScriptError(true);
          setIsLoading(false);
        }
      }
    };

    initChiliPiper();

    return () => {
      isMounted = false;
      // Only reset when the component unmounts, not on dependency changes
      resetChiliPiper();
    };
  }, [enabled, containerId, router, trigger]);

  return { isLoading, scriptError, reset };
};
